// Bulk-tag dialog — apply or remove a tag across all currently selected
// scans at once. Two modes toggled by a segmented control at the top:
//
//   • ADD: pick from most-used tags or type a new one; ADDS to each scan.
//   • REMOVE: shows the tags currently present on the selected scans as
//     checkboxes; ticked tags are REMOVED from each selected scan.
//
// Suggests the user's most-used tags first so they don't have to retype
// "back yard" every time.
import React, { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, Tag as TagIcon } from "lucide-react";
import { toast } from "sonner";
import { plants as plantsApi } from "@/lib/api";

export default function BulkTagDialog({ open, onOpenChange, scanIds, allItems, onDone }) {
  const { t } = useTranslation();
  const [mode, setMode] = useState("add"); // "add" | "remove"
  const [tagInput, setTagInput] = useState("");
  const [removeSelection, setRemoveSelection] = useState(() => new Set()); // lowercased tag keys
  const [busy, setBusy] = useState(false);

  // Reset local state whenever the dialog opens.
  useEffect(() => {
    if (open) {
      setMode("add");
      setTagInput("");
      setRemoveSelection(new Set());
      setBusy(false);
    }
  }, [open]);

  // Suggest the user's own most-frequently-used tags across their whole gallery.
  const suggestions = useMemo(() => {
    const counts = new Map();
    for (const it of allItems || []) {
      for (const raw of (it?.tags || [])) {
        const tt = String(raw || "").trim();
        if (!tt) continue;
        const key = tt.toLowerCase();
        counts.set(key, (counts.get(key) || 0) + 1);
      }
    }
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([lower]) => {
        for (const it of allItems || []) {
          for (const raw of (it?.tags || [])) {
            if (String(raw).toLowerCase() === lower) return raw;
          }
        }
        return lower;
      });
  }, [allItems]);

  // Tags present on the currently selected scans — only these can be removed
  // in one bulk call. Order by frequency within the selection so the most
  // impactful tags appear first.
  const selectedTags = useMemo(() => {
    const idSet = new Set(scanIds || []);
    const counts = new Map();
    const display = new Map();
    for (const it of allItems || []) {
      if (!idSet.has(it?.scan_id)) continue;
      for (const raw of (it?.tags || [])) {
        const tt = String(raw || "").trim();
        if (!tt) continue;
        const key = tt.toLowerCase();
        counts.set(key, (counts.get(key) || 0) + 1);
        if (!display.has(key)) display.set(key, tt);
      }
    }
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([key, count]) => ({ key, label: display.get(key) || key, count }));
  }, [allItems, scanIds]);

  const applyAdd = async (tagRaw) => {
    const tag = (tagRaw ?? tagInput).trim();
    if (!tag) return;
    if (scanIds.length === 0) {
      toast.error(t("bulkTag.emptySelection"));
      return;
    }
    setBusy(true);
    try {
      const { data } = await plantsApi.bulkTag(scanIds, [tag], []);
      onDone?.(data?.added_count ?? 0, tag, "add");
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("bulkTag.fail"));
    } finally {
      setBusy(false);
    }
  };

  const applyRemove = async () => {
    if (scanIds.length === 0) {
      toast.error(t("bulkTag.emptySelection"));
      return;
    }
    // Turn the lower-cased keys back into their display casing so the
    // toast reads like the user's tag rather than a mangled slug.
    const removes = Array.from(removeSelection)
      .map((key) => selectedTags.find((x) => x.key === key)?.label || key);
    if (removes.length === 0) return;
    setBusy(true);
    try {
      await plantsApi.bulkTag(scanIds, [], removes);
      onDone?.(scanIds.length, removes.join(", "), "remove");
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("bulkTag.fail"));
    } finally {
      setBusy(false);
    }
  };

  const toggleRemoveKey = (key) => {
    setRemoveSelection((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const canApplyRemove = mode === "remove" && !busy && removeSelection.size > 0;

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="max-w-md" data-testid="bulk-tag-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {mode === "add"
              ? t("bulkTag.title", { n: scanIds.length })
              : t("bulkTag.titleRemove", { n: scanIds.length })}
          </DialogTitle>
          <DialogDescription>
            {mode === "add" ? t("bulkTag.desc") : t("bulkTag.descRemove")}
          </DialogDescription>
        </DialogHeader>

        {/* Mode switch — add ↔ remove. Native radiogroup so keyboard nav
            reads correctly. */}
        <div
          className="inline-flex items-center rounded-full bg-parchment p-1 self-start"
          role="radiogroup"
          aria-label={t("bulkTag.modeAria")}
          data-testid="bulk-tag-mode-switch"
        >
          <ModeChip
            active={mode === "add"}
            onClick={() => setMode("add")}
            testid="bulk-tag-mode-add"
            label={t("bulkTag.modeAdd")}
          />
          <ModeChip
            active={mode === "remove"}
            onClick={() => setMode("remove")}
            testid="bulk-tag-mode-remove"
            label={t("bulkTag.modeRemove")}
          />
        </div>

        {mode === "add" && (
          <>
            {suggestions.length > 0 && (
              <div>
                <p className="text-xs uppercase tracking-[0.14em] text-leaf-mist mb-2">
                  {t("bulkTag.suggestionsLabel")}
                </p>
                <div className="flex flex-wrap gap-2" data-testid="bulk-tag-suggestions">
                  {suggestions.map((tag) => (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => applyAdd(tag)}
                      disabled={busy}
                      data-testid={`bulk-tag-suggestion-${String(tag).toLowerCase().replace(/\s+/g, "-")}`}
                      className="text-xs inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-parchment hover:bg-[#E8E1CE] text-leaf-ink border border-[#E8E5DF] transition disabled:opacity-50"
                    >
                      <TagIcon className="w-3 h-3" strokeWidth={1.5} /> {tag}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div>
              <label className="block text-xs uppercase tracking-[0.14em] text-leaf-mist mb-1.5">
                {t("bulkTag.newLabel")}
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value.slice(0, 32))}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyAdd(); } }}
                  placeholder={t("bulkTag.newPlaceholder")}
                  maxLength={32}
                  disabled={busy}
                  data-testid="bulk-tag-input"
                  className="flex-1 rounded-2xl border border-[#E8E5DF] bg-cream px-4 py-3 text-sm text-leaf-ink focus:outline-none focus:ring-2 focus:ring-leaf/40"
                />
                <button
                  type="button"
                  onClick={() => applyAdd()}
                  disabled={busy || !tagInput.trim()}
                  data-testid="bulk-tag-apply"
                  className="rounded-full px-4 py-3 bg-leaf text-white text-sm font-medium hover:bg-leaf-deep transition inline-flex items-center gap-2 disabled:opacity-60"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} /> : null}
                  {t("bulkTag.apply")}
                </button>
              </div>
            </div>
          </>
        )}

        {mode === "remove" && (
          <div>
            <p className="text-xs uppercase tracking-[0.14em] text-leaf-mist mb-2">
              {t("bulkTag.removeLabel")}
            </p>
            {selectedTags.length === 0 ? (
              <p className="text-sm text-leaf-mist" data-testid="bulk-tag-remove-empty">
                {t("bulkTag.removeEmpty")}
              </p>
            ) : (
              <ul
                className="flex flex-col gap-1 max-h-64 overflow-y-auto pr-1"
                data-testid="bulk-tag-remove-list"
              >
                {selectedTags.map(({ key, label, count }) => {
                  const checked = removeSelection.has(key);
                  return (
                    <li key={key}>
                      <label
                        className={`w-full flex items-center gap-2.5 rounded-2xl px-3 py-2 cursor-pointer transition ${
                          checked ? "bg-rust/10 border border-rust/30" : "bg-white border border-[#E8E5DF] hover:bg-parchment"
                        }`}
                        data-testid={`bulk-tag-remove-item-${key.replace(/\s+/g, "-")}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleRemoveKey(key)}
                          disabled={busy}
                          className="w-4 h-4 accent-rust"
                          data-testid={`bulk-tag-remove-check-${key.replace(/\s+/g, "-")}`}
                        />
                        <TagIcon className="w-3.5 h-3.5 text-leaf-mist shrink-0" strokeWidth={1.5} aria-hidden />
                        <span className="flex-1 text-sm text-leaf-ink truncate">{label}</span>
                        <span className="text-[11px] text-leaf-mist tabular-nums">
                          {t("bulkTag.removeItemCount", { count })}
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
            {selectedTags.length > 0 && (
              <div className="mt-3 flex justify-end">
                <button
                  type="button"
                  onClick={applyRemove}
                  disabled={!canApplyRemove}
                  data-testid="bulk-tag-remove-apply"
                  className="rounded-full px-4 py-2.5 bg-rust text-white text-sm font-medium hover:bg-rust/90 transition inline-flex items-center gap-2 disabled:opacity-50"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} /> : null}
                  {t("bulkTag.removeApply", { n: removeSelection.size })}
                </button>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            data-testid="bulk-tag-cancel"
            className="rounded-full px-5 py-2.5 text-sm bg-white border border-[#E8E5DF] text-leaf-ink hover:bg-parchment transition"
          >
            {t("common.cancel")}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ModeChip({ active, onClick, label, testid }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      onClick={onClick}
      data-testid={testid}
      className={`px-3 py-1.5 rounded-full text-[11px] uppercase tracking-[0.14em] font-medium transition-colors ${
        active ? "bg-leaf text-white shadow-sm" : "text-leaf-ink hover:bg-white/60"
      }`}
    >
      {label}
    </button>
  );
}
